import { createContext, useContext, useRef } from 'react'

import { useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

import {
  type EditTagsDialogStore,
  type EditTagsDialogStoreState,
  type InitEditTagsDialogStoreArgs,
  createEditTagsDialogStore,
} from '@/components/tags/EditTagsDialog/store'

import type { UUID } from '@/types/Id'

// region: dialog store context

const EditTagsDialogStoreContext = createContext<
  EditTagsDialogStore | undefined
>(undefined)

interface EditTagsDialogStoreContextProviderProps {
  children: React.ReactNode
  initStoreArgs?: InitEditTagsDialogStoreArgs
}

const EditTagsDialogStoreContextProvider: React.FC<
  EditTagsDialogStoreContextProviderProps
> = ({ children, initStoreArgs }) => {
  const storeRef = useRef<EditTagsDialogStore>(
    createEditTagsDialogStore(initStoreArgs ?? { tags: [] }),
  )
  if (!storeRef.current) {
    storeRef.current = createEditTagsDialogStore(initStoreArgs ?? { tags: [] })
  }
  return (
    <EditTagsDialogStoreContext.Provider value={storeRef.current}>
      {children}
    </EditTagsDialogStoreContext.Provider>
  )
}

interface EditTagsDialogContextProviderProps {
  children: React.ReactNode
  initStoreArgs?: InitEditTagsDialogStoreArgs
}

export const EditTagsDialogContextProvider: React.FC<
  EditTagsDialogContextProviderProps
> = ({ children, initStoreArgs }) => {
  return (
    <EditTagsDialogStoreContextProvider initStoreArgs={initStoreArgs}>
      {children}
    </EditTagsDialogStoreContextProvider>
  )
}

export const useEditTagsDialogStoreContext = () => {
  const context = useContext(EditTagsDialogStoreContext)
  if (!context) {
    throw new Error(
      'useEditTagsDialogStoreContext must be used within a EditTagsDialogStoreContextProvider',
    )
  }
  return context
}

export const useEditTagsDialogStore = <T,>(
  selector: (state: EditTagsDialogStoreState) => T = (state) => state as T,
) => {
  const context = useContext(EditTagsDialogStoreContext)
  if (!context) {
    throw new Error(
      'useEditTagsDialogStore must be used within a EditTagsDialogStoreContextProvider',
    )
  }
  return useStore(context, selector)
}

// Store state hooks
export const useEditTagsDialogEditableTags = () => {
  return useEditTagsDialogStore(useShallow((state) => state.editableTags))
}

export const useEditTagsDialogEditableTagIds = () => {
  return useEditTagsDialogStore(
    useShallow((state) => state.editableTags.map((tag) => tag.id)),
  )
}

export const useEditTagsDialogSelectedTagId = () => {
  return useEditTagsDialogStore((state) => state.selectedTagId)
}

export const useEditTagDialogTagIsSelected = (tagId: UUID) => {
  return useEditTagsDialogStore(
    useShallow((state) => state.selectedTagId === tagId),
  )
}

export const useEditTagsDialogSelectedTag = () => {
  const selectedTagId = useEditTagsDialogSelectedTagId()
  return useEditTagsDialogStore(
    useShallow((state) => {
      if (!selectedTagId) {
        return null
      }
      return state.editableTags.find((tag) => tag.id === selectedTagId)
    }),
  )
}

export const useEditTagsDialogTag = (tagId: UUID) => {
  return useEditTagsDialogStore(
    useShallow((state) => state.editableTags.find((tag) => tag.id === tagId)),
  )
}

export const useEditTagsDialogTagName = (tagId: UUID) => {
  return useEditTagsDialogStore(
    useShallow(
      (state) => state.editableTags.find((tag) => tag.id === tagId)?.name,
    ),
  )
}

export const useEditTagsDialogTagColor = (tagId: UUID) => {
  return useEditTagsDialogStore(
    useShallow(
      (state) => state.editableTags.find((tag) => tag.id === tagId)?.color,
    ),
  )
}

export const useEditTagsDialogTagDescription = (tagId: UUID) => {
  return useEditTagsDialogStore(
    useShallow(
      (state) =>
        state.editableTags.find((tag) => tag.id === tagId)?.description,
    ),
  )
}

export const useEditTagsDialogTagIsNew = (tagId: UUID) => {
  return useEditTagsDialogStore(
    useShallow(
      (state) => state.editableTags.find((tag) => tag.id === tagId)?.isNew,
    ),
  )
}

export const useEditTagsDialogValidationErrors = () => {
  return useEditTagsDialogStore(useShallow((state) => state.validationErrors))
}

export const useEditTagsDialogTagValidationError = (tagId: UUID) => {
  return useEditTagsDialogStore((state) => state.validationErrors[tagId])
}

export const useEditTagsDialogDeletedTagIds = () => {
  return useEditTagsDialogStore(useShallow((state) => state.deletedTagIds))
}

export const useEditTagsDialogTagType = () => {
  return useEditTagsDialogStore((state) => state.tagType)
}

// Action hooks
export const useEditTagsDialogInit = () => {
  return useEditTagsDialogStore((state) => state.init)
}

export const useEditTagsDialogSelectTag = () => {
  return useEditTagsDialogStore((state) => state.selectTag)
}

export const useEditTagsDialogUpdateTagDetails = () => {
  return useEditTagsDialogStore((state) => state.updateTagDetails)
}

export const useEditTagsDialogAddNewTag = () => {
  return useEditTagsDialogStore((state) => state.addNewTag)
}

export const useEditTagsDialogMarkTagForDeletion = () => {
  return useEditTagsDialogStore((state) => state.markTagForDeletion)
}

export const useEditTagsDialogMoveTag = () => {
  return useEditTagsDialogStore((state) => state.moveTag)
}

export const useEditTagsDialogValidateTags = () => {
  return useEditTagsDialogStore((state) => state.validateTags)
}

export const useEditTagsDialogClearDirtyState = () => {
  return useEditTagsDialogStore((state) => state.clearDirtyState)
}

export const useEditTagsDialogReset = () => {
  return useEditTagsDialogStore((state) => state.reset)
}

// Computed state hooks
export const useEditTagsDialogHasAnyChanges = () => {
  return useEditTagsDialogStore((state) => state.hasAnyChanges)
}

export const useEditTagsDialogHasChanges = () => {
  return useEditTagsDialogStore((state) => state.hasAnyChanges())
}

export const useEditTagsDialogGetTagChangeStatus = () => {
  return useEditTagsDialogStore((state) => state.getTagChangeStatus)
}

export const useEditTagsDialogTagChangeStatus = (tagId: UUID) => {
  return useEditTagsDialogStore(
    useShallow((state) => state.getTagChangeStatus(tagId)),
  )
}

export const useEditTagsDialogHasValidationErrors = () => {
  return useEditTagsDialogStore(
    (state) => Object.keys(state.validationErrors).length > 0,
  )
}

export const useEditTagsDialogCanSave = () => {
  return useEditTagsDialogStore(
    useShallow(
      (state) =>
        state.hasAnyChanges() &&
        !Object.values(state.validationErrors).some((error) => error),
    ),
  )
}

// endregion: dialog store context
